export const generateId = () =>
  Math.random().toString(36).substring(2, 8) + Date.now().toString(36);

export const getOrCreate = (key) => {
  const existing = localStorage.getItem(key);
  if (existing) return existing;
  const created = generateId();
  localStorage.setItem(key, created);
  return created;
};

const tryParseJson = (s) => {
  if (typeof s !== "string") return null;
  const t = s.trim();
  if (!t) return null;
  try {
    return JSON.parse(t);
  } catch {
    return null;
  }
};

const unescapeJsonString = (s) => {
  if (typeof s !== "string") return "";
  return s.replace(/\\"/g, '"').replace(/\\n/g, "\n").replace(/\\t/g, "\t").replace(/\\\\/g, "\\");
};

const stripFence = (s) => {
  let t = String(s || "").trim();
  t = t.replace(/^```(?:json)?\s*/i, "").replace(/\s*```$/i, "").trim();
  const start = t.indexOf("{");
  const end = t.lastIndexOf("}");
  if (start !== -1 && end > start && (t.includes('"text"') || t.includes('"emotion"'))) {
    return t.slice(start, end + 1);
  }
  return t;
};

const extractTextByScan = (raw) => {
  if (typeof raw !== "string") return null;
  const s = raw;
  const keyIdx = s.indexOf('"text"');
  if (keyIdx === -1) return null;
  let i = keyIdx + '"text"'.length;
  while (i < s.length && s[i] !== ":") i++;
  if (i >= s.length) return null;
  i++;
  while (i < s.length && /\s/.test(s[i])) i++;
  if (s[i] !== '"') return null;
  i++;
  let out = "";
  let escaped = false;
  for (; i < s.length; i++) {
    const ch = s[i];
    if (escaped) {
      out += "\\" + ch;
      escaped = false;
      continue;
    }
    if (ch === "\\") {
      escaped = true;
      continue;
    }
    if (ch === '"') return unescapeJsonString(out).trim() || null;
    out += ch;
  }
  return unescapeJsonString(out).trim() || null;
};

const looksLikePayload = (s) => {
  if (typeof s !== "string") return false;
  const t = s.trim();
  return (
    t.startsWith("```") ||
    (t.includes("{") && /"(?:text|emotion|voice_style|animation|type)"/.test(t) && t.includes("}"))
  );
};

export const unwrapReply = (rawContent) => {
  let cur = rawContent;
  let emotion;
  let voice_style;
  let animation;

  for (let i = 0; i < 5; i++) {
    if (cur && typeof cur === "object") {
      emotion = cur.emotion || emotion;
      voice_style = cur.voice_style || voice_style;
      animation = cur.animation || animation;
      const inner = cur.text ?? cur.message ?? cur.content;
      if (typeof inner === "string" && looksLikePayload(inner)) {
        cur = stripFence(inner);
        continue;
      }
      if (typeof inner === "string" && inner.trim()) {
        return { text: inner.trim(), emotion, voice_style, animation };
      }
      return { text: "", emotion, voice_style, animation };
    }

    if (typeof cur !== "string") break;
    const stripped = stripFence(cur);
    const parsed = tryParseJson(stripped);
    if (parsed && typeof parsed === "object") {
      cur = parsed;
      continue;
    }
    const scanned = extractTextByScan(stripped);
    const em = stripped.match(/"emotion"\s*:\s*"((?:\\.|[^"\\])*)"/s)?.[1];
    const vs = stripped.match(/"voice_style"\s*:\s*"((?:\\.|[^"\\])*)"/s)?.[1];
    const an = stripped.match(/"animation"\s*:\s*"((?:\\.|[^"\\])*)"/s)?.[1];
    return {
      text: (scanned || stripped).trim(),
      emotion: em ? unescapeJsonString(em) : emotion,
      voice_style: vs ? unescapeJsonString(vs) : voice_style,
      animation: an ? unescapeJsonString(an) : animation,
    };
  }

  const fallback = typeof cur === "string" ? cur.trim() : "";
  return { text: fallback, emotion, voice_style, animation };
};

export const extractAssistantMeta = (rawContent) => {
  const reply = unwrapReply(rawContent);
  if (!reply.emotion && !reply.voice_style && !reply.animation && !reply.text) return null;
  return reply;
};

export const extractDisplayText = (role, content) => {
  if (role === "user") {
    const s = typeof content === "string" ? content : String(content ?? "");
    return s.trim() ? s : "[empty user message]";
  }
  const text = unwrapReply(content).text;
  if (text && !looksLikePayload(text)) return text;
  if (text) return unwrapReply(text).text || text;
  return "[empty assistant message]";
};

export const spokenText = (text) => {
  const clean = unwrapReply(text).text || String(text || "");
  return clean
    .replace(/\[\[IMAGE:[^\]]+\]\]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
};
